import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useToast } from '../../../context/ToastContext';
import { retryReceiptExtraction } from '../apis/uploadedReceiptApi';

/**
 * Hook to retry text extraction for a single failed receipt.
 * On success, invalidates the uploaded-receipts cache so polling picks up the status change.
 */
export const useRetryReceiptExtraction = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const mutation = useMutation({
    mutationFn: async (receiptId: string) => {
      const token = await getAccessTokenSilently();
      return retryReceiptExtraction(receiptId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['uploaded-receipts'] });
    },
  });

  const retryExtraction = useCallback(
    async (receiptId: string): Promise<boolean> => {
      try {
        await mutation.mutateAsync(receiptId);
        return true;
      } catch (error) {
        console.error('Failed to retry extraction:', error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to retry text extraction. Please try again.';
        showToast('Retry Failed', errorMessage, 'error');
        return false;
      }
    },
    [mutation, showToast]
  );

  return {
    retryExtraction,
    isRetrying: mutation.isPending,
  };
};
